// ProcedurePage.tsx
import {
	FC,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useLocation } from "react-router";
import { useNavigate, useParams } from "react-router-dom";
import Split from "react-split";

import { ProcedureContext } from "@/context/ProcedureContext";
import {
	Box,
	Paper,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { BreadCrumbProcedureSingle } from "@/components/Application/BreadCrumbsDesign";
import { WrapperLeft } from "@/components/Application/WrapperLeft";
import { WrapperRight } from "@/components/Application/WrapperRight";
import { useFetchChecklist } from "@/components/Procedures/API/ChecklistQueries";
import { AccordionSingle } from "@/components/Procedures/AccordionSingle";
import {
	Checklist,
	ChecklistBody,
	ChecklistHeader,
	ChecklistHeadingTools,
} from "@/components/Procedures/Checklist/Checklist";
import { ChecklistNavigationControls } from "@/components/Procedures/Checklist/ChecklistNavigationControls";
import { HeaderTools } from "@/components/Procedures/Checklist/utils/Types";
import { CompleteProcedure } from "@/components/Procedures/CompleteProcedureModal";
import ChecklistPreview from "@/components/Procedures/Components/CreateStep/ChecklistPreview";
import { ImportedChecklistEditorContainer } from "@/components/Procedures/Containers/ImportedChecklistEditorContainer/ImportedChecklistEditorContainer";
import ManualChecklistEditorContainer from "@/components/Procedures/Containers/ManualChecklistEditorContainer";
import { NodeView } from "@/components/Procedures/EditNode/EditNode";
import { EditProcedureModal } from "@/components/Procedures/EditProcedure";
import { HistoryDrawer } from "@/components/Procedures/HistoryDrawer";
import { ProcedureActions } from "@/components/Procedures/ProcedureActions";

import { ProcedureDetailNode } from "@/model/Procedure/ProcedureDetailNode";

import { useQueryParamHide } from "@/service/Hooks";
import {
	useFetchActiveProcedure,
	useFetchActiveProcedureDetails,
	useFetchActiveProcedureGroupFolders,
	useHandleProcedureDetails,
} from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

interface ProcedureProps {
	editMode?: boolean;
	editModeWizard?: boolean;
	newPageWizard?: boolean;
	editNode?: boolean;
}

export const Procedure: FC<ProcedureProps> = (props) => {
	/* ─────────────── State ─────────────── */
	const [hide, setHide] = useState(false);
	const [hideChecklist, setHideChecklist] = useState<boolean>(false);
	const [openDetailNodes, setOpenDetailNodes] = useState<Set<number>>(
		new Set(),
	);
	const [historyOpen, toggleHistory] = useState<boolean>(false);
	const [completeOpen, toggleComplete] = useState<boolean>(false);
	const [editOpen, toggleEdit] = useState<boolean>(false);
	const [editChecklist, toggleEditChecklist] = useState<boolean>(false);
	const [editNode, toggleEditNode] = useState<boolean>(false);
	const [paginationEnabled, setPaginationEnabled] = useState(true);
	const [hasNavigatedToChecklist, setHasNavigatedToChecklist] = useState(false);

	/* ─────────────── Routing / location ─────────────── */
	const { id, schemeId: checklistIdUrlParam } = useParams<RouteParams>();
	const activeChecklistId = checklistIdUrlParam
		? parseInt(checklistIdUrlParam)
		: undefined;
	const location = useLocation();
	const navigate = useNavigate();

	/* ─────────────── Data ─────────────── */
	const { data: checklist } = useFetchChecklist(activeChecklistId || 0);
	const { data: procedure } = useFetchActiveProcedure();
	const { groupList } = useFetchActiveProcedureGroupFolders();

	const { setOpenFolders } = useContext(ProcedureContext);
	const queryHide = useQueryParamHide();

	const { data: ProcedureDetails, isLoading } =
		useFetchActiveProcedureDetails();
	const { activeChildNode, activeParentNode } = useHandleProcedureDetails(
		ProcedureDetails?.ProcedureDetailNodes,
		activeChecklistId,
	);

	/* ─────────────── UI hooks (top-level only) ─────────────── */
	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down("sm"));

	// Persist split sizes between loads
	const splitKey = "procedure-split-sizes";
	const defaultSizes = useMemo<number[]>(() => [32, 68], []); // stable default
	const [sizes, setSizes] = useState<number[]>(
		() => JSON.parse(localStorage.getItem(splitKey) || "null") || defaultSizes,
	);

	const handleDragEnd = (next: number[]) => {
		setSizes(next);
		localStorage.setItem(splitKey, JSON.stringify(next));
	};

	/* ─────────────── Helpers ─────────────── */
	const findAllParentFolderIds = useCallback(
		(groupId: number): string[] => {
			if (!groupList?.length || !groupId) return [];
			const ids: string[] = [];
			let current = groupList.find((g) => g.Id === groupId);
			while (current) {
				ids.push(String(current.Id));
				if (!current.ParentId || current.ParentId === 0) break;
				current = groupList.find((g) => g.Id === current!.ParentId);
			}
			return ids;
		},
		[groupList],
	);

	const toggleOpenDetailNodes = useCallback(
		(node: ProcedureDetailNode, forceOpen = false) => {
			if (!node) return;
			setOpenDetailNodes((prev) => {
				const next = new Set(prev);
				if (forceOpen) {
					next.add(node.TreeNodeNumber);
					return next;
				}
				if (next.has(node.TreeNodeNumber)) next.delete(node.TreeNodeNumber);
				else next.add(node.TreeNodeNumber);
				return next;
			});
		},
		[],
	);

	const closeAllOpenDetailNodes = useCallback(
		() => setOpenDetailNodes(new Set()),
		[],
	);
	const openAllOpenDetailNodes = useCallback(
		(ids: Array<number> | undefined) => {
			if (!ids) return;
			setOpenDetailNodes(new Set(ids));
		},
		[],
	);

	// IMPORTANT: always preserve current query string (e.g. ?hide=1) when navigating
	const preserveSearch = location.search; // includes "?hide=1" if present

	const navigateToChecklistUrl = useCallback(
		(schemeId?: number) => {
			if (!schemeId) return;
			navigate({
				pathname: `/procedure/${id}/checklist/${schemeId}`,
				search: preserveSearch, // keep ?hide=1 (or others)
			});
		},
		[id, navigate, preserveSearch],
	);

	const nodeStepIds = useMemo(
		() =>
			isLoading
				? []
				: (ProcedureDetails?.ProcedureDetailNodes?.map(
						(s) => s.TreeNodeNumber,
					) ?? []),
		[isLoading, ProcedureDetails?.ProcedureDetailNodes],
	);

	/* ─────────────── Effects ─────────────── */
	// Expand folder tree to show the current procedure's group
	useEffect(() => {
		if (!procedure || !groupList?.length) return;
		const openIds = findAllParentFolderIds(procedure.GroupId);
		if (openIds.length) {
			setOpenFolders((prevFolders) => {
				// Merge existing folders with new ones, removing duplicates
				const merged = new Set([...prevFolders, ...openIds]);
				return Array.from(merged);
			});
		}
	}, [procedure, groupList, findAllParentFolderIds, setOpenFolders]);

	// Toggle compact “hide” layout & hideChecklist when editing node
	useEffect(() => {
		setHide(queryHide);
		setHideChecklist(Boolean(editNode));
	}, [queryHide, editNode, activeChecklistId]);

	// Reset navigation flag when procedure ID changes
	useEffect(() => {
		setHasNavigatedToChecklist(false);
	}, [id]);

	// Auto navigate to first child node's checklist if none selected
	useEffect(() => {
		if (
			activeChildNode &&
			!checklistIdUrlParam &&
			!location.pathname.includes("checklist") &&
			location.pathname === `/procedure/${id}` &&
			!hasNavigatedToChecklist
		) {
			setHasNavigatedToChecklist(true);
			navigate(
				{
					pathname: `/procedure/${id}/checklist/${activeChildNode.SchemeId}`,
					search: preserveSearch, // keep the query
				},
				{ replace: true },
			);
		}
	}, [
		activeChildNode,
		checklistIdUrlParam,
		location.pathname,
		navigate,
		id,
		preserveSearch,
		hasNavigatedToChecklist,
	]);

	// Ensure parent node is expanded in the left accordion
	useEffect(() => {
		if (activeParentNode) toggleOpenDetailNodes(activeParentNode, true);
	}, [activeParentNode, toggleOpenDetailNodes]);

	/* ─────────────── UI bits ─────────────── */
	const LeftHeader = (
		<Box
			sx={{
				position: "sticky",
				top: 0,
				zIndex: 1,
				pb: 1.5,
				mb: 1.5,
				bgcolor: "background.paper",
			}}
		>
			<Stack
				direction="row"
				alignItems="center"
				spacing={1.5}
				sx={{ minHeight: 42 }}
			>
				<BreadCrumbProcedureSingle
					trainingType={ProcedureDetails?.TrainingType}
					procedureState={ProcedureDetails?.ProcedureState}
					parrentprocedureid={ProcedureDetails?.ParrentProcedureId}
				/>
				<Box sx={{ flex: 1 }} />
				<ProcedureActions
					procedure={procedure}
					nodeStepIds={nodeStepIds}
					toggleHistory={toggleHistory}
					CloseAllOpenDetailNodes={closeAllOpenDetailNodes}
					OpenAllOpenDetailNodes={openAllOpenDetailNodes}
					toggleComplete={toggleComplete}
					setActiveSchemeId={navigateToChecklistUrl}
					setOpenDetailNodes={setOpenDetailNodes}
					setEditChecklist={toggleEditChecklist}
				/>
			</Stack>

			{!editChecklist && (
				<Typography variant="h5" fontWeight={800} sx={{ mt: 1 }}>
					{procedure?.Name}
				</Typography>
			)}
		</Box>
	);

	/* ─────────────── Panes ─────────────── */
	const LeftPane = (
		<Box
			sx={{
				height: "100%",
				minWidth: 420,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
			}}
		>
			<WrapperLeft
				className={`wrapper-scroll ${editChecklist ? "wrapper--half" : "wrapper--small"}`}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						flex: 1,
						minHeight: 0,
					}}
				>
					{LeftHeader}

					{editChecklist ? (
						<ChecklistPreview checklistId={activeChecklistId} />
					) : (
						<AccordionSingle
							editMode={props.editMode}
							openDetailNodes={openDetailNodes}
							activeSchemeId={activeChecklistId ?? 0}
							setActiveSchemeId={navigateToChecklistUrl}
							toggleOpenDetailNodes={toggleOpenDetailNodes}
							readonly={false}
							showSingleSchemeId={0}
							toggleEditNode={toggleEditNode}
							toggleEditChecklist={toggleEditChecklist}
							paginationEnabled={paginationEnabled}
							setPaginationEnabled={setPaginationEnabled}
						/>
					)}
				</Box>
			</WrapperLeft>
		</Box>
	);

	const RightPane = (
		<Box
			sx={{ height: "100%", minWidth: 600, minHeight: 0, overflow: "hidden" }}
		>
			<WrapperRight
				className={`wrapper-scroll ${editChecklist ? "wrapper--half no-margin" : "wrapper--big"}`}
			>
				<Paper>
					<Checklist hide={hideChecklist}>
						<ChecklistHeader
							activeSchemeId={activeChecklistId}
							editMode={editChecklist}
						>
							<ChecklistHeadingTools
								activeTools={
									new Set<HeaderTools>([
										"edit",
										"fullscreen",
										"navigation",
										"newManualChecklist",
										"newNodeImportedChecklist",
										"openInNewWindow",
									])
								}
								activeSchemeId={activeChecklistId}
								checklistProcedureType="procedure"
								editMode={editChecklist}
								SetActiveSchemeId={navigateToChecklistUrl}
								setEditMode={toggleEditChecklist}
							/>
						</ChecklistHeader>

						<ChecklistBody
							activeSchemeId={activeChecklistId}
							editMode={editChecklist}
						/>

						<AdminProtected>
							{editChecklist &&
								(checklist?.ImportedChecklist ? (
									<ImportedChecklistEditorContainer
										activeSchemeId={activeChecklistId}
										checklistProcedureType="procedure"
										editMode={editChecklist}
										newpage={props.newPageWizard}
									/>
								) : (
									<ManualChecklistEditorContainer
										activeChecklistId={activeChecklistId}
									/>
								))}
						</AdminProtected>

						<ChecklistNavigationControls
							activeSchemeId={activeChecklistId}
							setActiveSchemeId={navigateToChecklistUrl}
							wrapControls
						/>
					</Checklist>
				</Paper>

				<NodeView
					node={activeParentNode}
					edit={editNode}
					toggleEditNode={toggleEditNode}
				/>
			</WrapperRight>
		</Box>
	);

	/* ─────────────── Render (single return, no early exits) ─────────────── */
	return (
		<>
			<Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
				<Box sx={{ flex: "1 1 auto", minHeight: 0 }}>
					{/* Compact "hide" layout just swaps the whole shell */}
					{hide ? (
						<Box>
							<WrapperLeft className="wrapper--half">
								<Checklist hide={hideChecklist}>
									<ChecklistHeader
										activeSchemeId={activeChecklistId}
										editMode={editChecklist}
									>
										<ChecklistHeadingTools
											activeTools={
												new Set<HeaderTools>(["navigation", "fullscreen"])
											}
											activeSchemeId={activeChecklistId}
											checklistProcedureType="procedure"
											editMode={editChecklist}
											SetActiveSchemeId={navigateToChecklistUrl}
										/>
									</ChecklistHeader>

									<ChecklistBody
										activeSchemeId={activeChecklistId}
										editMode={props.editMode}
									/>

									<Box sx={{ mt: 1 }}>
										<ChecklistNavigationControls
											activeSchemeId={activeChecklistId}
											setActiveSchemeId={navigateToChecklistUrl}
										/>
									</Box>
								</Checklist>

								<NodeView
									node={activeParentNode}
									edit={editNode}
									toggleEditNode={toggleEditNode}
								/>
							</WrapperLeft>
						</Box>
					) : isXs ? (
						<Box sx={{ display: "grid", gap: 2 }}>
							{LeftPane}
							{RightPane}
						</Box>
					) : (
						<Split
							className="split"
							sizes={sizes}
							minSize={[240, 320]}
							gutterSize={8}
							snapOffset={24}
							onDragEnd={handleDragEnd}
						>
							{LeftPane}
							{RightPane}
						</Split>
					)}
				</Box>
			</Box>

			{/* Drawers / Modals */}
			<HistoryDrawer historyOpen={historyOpen} toggleHistory={toggleHistory} />
			<CompleteProcedure
				open={completeOpen}
				onClose={() => toggleComplete(false)}
			/>
			<EditProcedureModal
				open={editOpen}
				onClose={() => toggleEdit(false)}
				id={id ? +id : 0}
			/>
		</>
	);
};

export type checklistProcedureType = "procedure" | "materialhandling";
